/**
 * SEE-219: inactivity session timeout — pure helpers.
 *
 * ONE control: a sliding inactivity timer, always on for every user.
 * Activity (clicks, keys, scrolls, touches) resets it; after the configured
 * idle window the user is logged out, with the "Session time out" warning
 * dialog shown for the final 10 minutes.
 *
 * The duration resolves as:
 *   1. org admin setting (Paidol.sessionTimeoutSettings, admin-only CDK
 *      updatePaidolSettings mutation) — applies to everyone in the paidol;
 *      multi-company users get the timeout of their SELECTED company,
 *   2. else the platform default.
 * Min / max / default are engineering-tunable PER PLATFORM (web vs mobile
 * shell) through the `sessiontimeoutconfig` feature flag; every field falls
 * back to the hardcoded values below when the flag is absent or partial, so
 * a missing AppConfig entry can never disable enforcement.
 *
 * Last activity is tracked in localStorage so it is shared across tabs and
 * survives the mobile shell's backup/restore of WebView localStorage. The key
 * names deliberately avoid the token/session/auth patterns that
 * `isSensitiveKey` (authSlice) excludes from the mobile backup.
 */

export const DEFAULT_TIMEOUT_MINUTES = 1440; // 24 hours
export const DEFAULT_MIN_TIMEOUT_MINUTES = 30;
export const DEFAULT_MAX_TIMEOUT_MINUTES = 1440; // 24 hours

/**
 * Hard code-level bounds that NO flag or org value can escape. The floor
 * exists because a timeout <= 0 would expire every session instantly on
 * every check — a single bad flag deploy must never be able to lock the
 * whole platform out. The ceiling matches the server-side sanity bound
 * (30 days, the refresh-token lifetime).
 */
export const HARD_MIN_TIMEOUT_MINUTES = 5;
export const HARD_MAX_TIMEOUT_MINUTES = 43200; // 30 days

/** How long the "Session time out" warning dialog shows before logout. */
export const SESSION_TIMEOUT_WARNING_SECONDS = 600; // 10 minutes
/** How often the guard re-evaluates idle state. */
export const SESSION_TIMEOUT_CHECK_INTERVAL_MS = 15 * 1000;
/** Minimum gap between localStorage writes from activity events. */
export const ACTIVITY_WRITE_THROTTLE_MS = 30 * 1000;

export const LAST_ACTIVITY_STORAGE_KEY = 'lastUserActivityAt';
/**
 * Cache of the last resolved timeout so the boot check can run before the
 * paidol (and its org setting) has loaded.
 */
export const EFFECTIVE_TIMEOUT_STORAGE_KEY = 'inactivityTimeoutMinutes';
/**
 * Stamped at the moment an idle logout fires. Other open tabs react to the
 * `storage` event on this key so a logout in one tab flips every tab to the
 * logged-out screen immediately.
 */
export const IDLE_LOGOUT_STORAGE_KEY = 'idleLogoutAt';

export interface SessionTimeoutSettingLike {
  isEnabled?: boolean | null;
  timeoutMinutes?: number | null;
}

export interface SessionTimeoutConfig {
  minMinutes: number;
  maxMinutes: number;
  defaultMinutes: number;
}

const asFiniteNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Round to whole minutes and force into the hard [5, 43200] bounds. */
const toSaneMinutes = (value: number): number =>
  Math.min(HARD_MAX_TIMEOUT_MINUTES, Math.max(HARD_MIN_TIMEOUT_MINUTES, Math.round(value)));

/**
 * Resolve the platform's min/max/default from the `sessiontimeoutconfig`
 * feature flag value.
 *
 * AppConfig feature-flag attributes are FLAT typed scalars (the flag arrives
 * as e.g. `{ enabled: true, webDefaultMinutes: 480, mobileMinMinutes: 15 }`),
 * so the canonical shape is platform-prefixed number attributes:
 *   webMinMinutes / webMaxMinutes / webDefaultMinutes
 *   mobileMinMinutes / mobileMaxMinutes / mobileDefaultMinutes
 * A nested `{ web: { minMinutes, ... } }` object is tolerated as a fallback.
 * Every field is optional and falls back independently, so engineering can
 * override e.g. only the mobile default.
 */
export const resolveSessionTimeoutConfig = (
  flagValue: unknown,
  platform: 'web' | 'mobile'
): SessionTimeoutConfig => {
  const source = flagValue && typeof flagValue === 'object' ? (flagValue as Record<string, unknown>) : {};
  const platformValue = source[platform];
  const nested =
    platformValue && typeof platformValue === 'object' ? (platformValue as Record<string, unknown>) : {};
  const pick = (flatSuffix: string, nestedKey: string): number | null =>
    asFiniteNumber(source[`${platform}${flatSuffix}`]) ?? asFiniteNumber(nested[nestedKey]);

  // Every value is rounded and forced into the hard bounds, and the trio is
  // made internally consistent (min <= default <= max) — ANY combination of
  // flag numbers yields a safe, working config.
  const minMinutes = toSaneMinutes(pick('MinMinutes', 'minMinutes') ?? DEFAULT_MIN_TIMEOUT_MINUTES);
  const maxMinutes = Math.max(
    minMinutes,
    toSaneMinutes(pick('MaxMinutes', 'maxMinutes') ?? DEFAULT_MAX_TIMEOUT_MINUTES)
  );
  const defaultMinutes = Math.min(
    maxMinutes,
    Math.max(minMinutes, toSaneMinutes(pick('DefaultMinutes', 'defaultMinutes') ?? DEFAULT_TIMEOUT_MINUTES))
  );
  return { minMinutes, maxMinutes, defaultMinutes };
};

/**
 * The effective idle window in minutes — ALWAYS a number; the timer is never
 * off. An enabled org setting is clamped to the platform's [min, max];
 * disabled/absent falls back to the platform default.
 */
export const resolveEffectiveTimeoutMinutes = (
  orgSetting: SessionTimeoutSettingLike | null | undefined,
  config: SessionTimeoutConfig
): number => {
  const orgMinutes = orgSetting?.isEnabled ? asFiniteNumber(orgSetting.timeoutMinutes) : null;
  if (orgMinutes === null) {
    return config.defaultMinutes;
  }
  return Math.min(config.maxMinutes, Math.max(config.minMinutes, Math.round(orgMinutes)));
};

export const isIdleExpired = (nowMs: number, lastActivityAtMs: number, timeoutMinutes: number): boolean =>
  nowMs - lastActivityAtMs >= timeoutMinutes * 60 * 1000;

/**
 * The boot check must only enforce a persisted activity stamp that belongs to
 * the CURRENT session — i.e. activity recorded AFTER this login's `auth_time`
 * (a restored web tab or a mobile cold-start). A stamp OLDER than the login
 * is leftover from a previous session (e.g. an SSO sign-out redirect unloaded
 * the page before cleanup ran) and judging a fresh login by it produced a
 * permanent login→logout lockout loop for SSO users. Fresh logins are NEVER
 * killed at boot, no matter what's in storage.
 *
 * authTimeMs null (token unreadable) fails open: no boot enforcement, the
 * periodic check takes over from a fresh stamp.
 */
export const shouldEnforceBootIdle = (
  nowMs: number,
  lastActivityAtMs: number | null,
  authTimeMs: number | null,
  timeoutMinutes: number
): boolean =>
  lastActivityAtMs !== null &&
  authTimeMs !== null &&
  lastActivityAtMs >= authTimeMs &&
  isIdleExpired(nowMs, lastActivityAtMs, timeoutMinutes);

// localStorage access is wrapped so a blocked/unavailable storage (Safari
// private mode, storage quota) degrades to "no tracking" instead of crashing.

export const readLastActivityAt = (): number | null => {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
    const parsed = raw === null ? NaN : Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const writeLastActivityAt = (nowMs: number): void => {
  try {
    localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(nowMs));
  } catch {
    // ignore — enforcement degrades gracefully without persisted activity
  }
};

export const clearLastActivityAt = (): void => {
  try {
    localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
  } catch {
    // ignore
  }
};

/** undefined = nothing cached yet. */
export const readEffectiveTimeoutMinutes = (): number | undefined => {
  try {
    const raw = localStorage.getItem(EFFECTIVE_TIMEOUT_STORAGE_KEY);
    if (raw === null) return undefined;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  } catch {
    return undefined;
  }
};

export const writeEffectiveTimeoutMinutes = (minutes: number): void => {
  try {
    localStorage.setItem(EFFECTIVE_TIMEOUT_STORAGE_KEY, String(minutes));
  } catch {
    // ignore
  }
};

/** Stamp the idle-logout marker; fires `storage` events in other open tabs. */
export const markIdleLogout = (nowMs: number): void => {
  try {
    localStorage.setItem(IDLE_LOGOUT_STORAGE_KEY, String(nowMs));
  } catch {
    // ignore
  }
};

/** Format remaining ms as mm:ss for the warning dialog (e.g. "01:36"). */
export const formatCountdown = (remainingMs: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};
